import { onUnmounted, Ref, watch } from '@vue/composition-api'
import * as Comlink from 'comlink'
import type { ExposeIframeOptions } from '../types/iframe'

/**
 * 暴露对象给 iframe 子应用调用
 * 用于父应用将 API 暴露给 iframe 子应用
 *
 * 注意：此功能需要安装 comlink 依赖
 * yarn add comlink
 * yarn add -D @types/comlink
 *
 * @param target - 要暴露的对象
 * @param iframeRef - iframe 元素的 ref
 * @param options - 配置选项
 *
 * @example
 * ```ts
 * const parentApi = {
 *   async getData() {
 *     return { data: 'hello' }
 *   }
 * }
 * const iframeRef = ref<HTMLIFrameElement | null>(null)
 * useExposeToChildIframe(parentApi, iframeRef)
 * ```
 */
export function useExposeToChildIframe<T extends Record<string, unknown>>(
  target: T,
  iframeRef: Ref<HTMLIFrameElement | null>,
  options?: ExposeIframeOptions
) {
  const targetOrigin = options?.targetOrigin || '*'

  watch(
    () => iframeRef.value,
    (iframe) => {
      if (iframe?.contentWindow) {
        // 使用 Comlink.windowEndpoint 创建 iframe 通信端点
        const endpoint = Comlink.windowEndpoint(iframe.contentWindow, window, targetOrigin)
        Comlink.expose(target, endpoint)
        console.debug('[useExposeToChildIframe] API exposed to child iframe')
      }
    },
    { immediate: true }
  )

  onUnmounted(() => {
    console.debug('[useExposeToChildIframe] Component unmounted')
  })

  return {
    exposed: target,
  }
}
